// React
import { useState } from 'react';

// Store
import useAuthStore from '../../../store/authStore.js';

// Components
import { Box, Button, IconButton, Paper, Popover, Typography, useMediaQuery } from "@mui/material";
import { LoginOutlined, HelpOutline, DarkModeOutlined, LightModeOutlined } from '@mui/icons-material';
import LoginForm from './LoginForm.js';
import CustomSnackbar from '../../custom/CustomSnackbar.js';

// Style
// Hooks
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Assets
import logo from '../../../assets/logo_SIMON_R4.png'
import banner from '../../../assets/banner_trans_SIMON.png'

// Bundestrainer E-Mail (hardcoded)
const NATIONAL_COACH_EMAIL = "oliver.pettke@dsqv.de";

const Login = () => {
	const { login } = useAuthStore();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [message, setMessage] = useState("");
	const [openFail, setOpenFail] = useState(false);
	const [loginFailed, setLoginFailed] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);

	// Theme
	const { isDarkMode, toggleDarkMode, colors, darkTheme: activeDarkTheme, lightTheme: activeLightTheme } = useAppTheme();

	const mobile = useMediaQuery("(max-width:600px)");

	const onCloseFail = () => {
		setOpenFail(false);
	}

	const handleHelpClick = (event) => {
		setAnchorEl(event.currentTarget);
	}

	const handleHelpClose = () => {
		setAnchorEl(null);
	}

	const openHelp = Boolean(anchorEl);

	const doLogin = () => {
		setLoginFailed(false);
		login(email, password).then(res => {
			if (!res) {
				setMessage("Login fehlgeschlagen!");
				setOpenFail(true);
				setLoginFailed(true);
			}
		})
	}



	return (
		<Box sx={{
			minHeight: '100vh',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			p: 2,
			background: colors.login.bg,
			backgroundSize: 'cover',
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			position: 'relative',
			overflow: 'hidden',
			transition: 'background 0.3s ease',
		}}>

			{/* BOTH blur layers ALWAYS rendered - only opacity switches (no DOM recreation = no blur recalculation) */}

			{/* Dark mode blur layer */}
			<Box sx={{
				position: 'absolute',
				top: '-100px', left: '-100px', right: '-100px', bottom: '-100px',
				background: activeDarkTheme.login.blur,
				filter: 'blur(40px)',
				backgroundBlendMode: 'screen, screen, normal',
				zIndex: 0,
				opacity: isDarkMode ? 1 : 0,
				visibility: isDarkMode ? 'visible' : 'hidden',
				transition: 'opacity 0.3s ease',
				pointerEvents: 'none',
			}} />

			{/* Light mode blur layer */}
			<Box sx={{
				position: 'absolute',
				top: '-100px', left: '-100px', right: '-100px', bottom: '-100px',
				background: activeLightTheme.login.blur,
				filter: 'blur(50px)',
				zIndex: 0,
				opacity: isDarkMode ? 0 : 1,
				visibility: isDarkMode ? 'hidden' : 'visible',
				transition: 'opacity 0.3s ease',
				pointerEvents: 'none',
			}} />

			{/* Noise overlay (always visible) */}
			<Box sx={{
				position: 'absolute',
				top: 0, left: 0, right: 0, bottom: 0,
				pointerEvents: 'none',
				backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='${isDarkMode ? '0.03' : '0.02'}'/%3E%3C/svg%3E")`,
				zIndex: 1,
			}} />



			<CustomSnackbar
				open={openFail}
				onClose={onCloseFail}
				message={message}
				severity="error"
				autoHideDuration={4000}
			/>

			{mobile ? (
				<Paper
					sx={{
						mt: -16,
						width: '90%',
						maxWidth: '400px',
						minHeight: 'auto',
						borderRadius: 4,
						position: 'relative',
						zIndex: 1,
						backgroundColor: isDarkMode ? 'rgba(20, 20, 20, 0.5)' : 'rgba(255, 255, 255, 0.3)',
						border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.3)',
						boxShadow: isDarkMode ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)' : '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
						p: 3,
						transition: 'background-color 0.2s ease, border-color 0.2s ease',
					}}
				>
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 2,
						minHeight: '100%',
					}}>
						{/* Dark Mode Toggle Button (Mobile) */}
						<IconButton
							onClick={toggleDarkMode}
							sx={{
								position: 'absolute',
								top: 16,
								right: 16,
								zIndex: 10,
								backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.6)' : 'rgba(0, 0, 0, 0.06)',
								color: isDarkMode ? colors.accentLight : 'text.secondary',
								border: '1px solid',
								borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
								boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
								'&:hover': {
									backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.8)' : 'rgba(0, 0, 0, 0.1)',
									transform: 'scale(1.05)',
								},
								transition: 'background-color 0.2s ease, transform 0.2s ease',
							}}
						>
							{isDarkMode ? <LightModeOutlined /> : <DarkModeOutlined />}
						</IconButton>
						{/* Logo and Banner */}
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%', mb: 1 }}>
							<img alt="logo" style={{ width: 90, height: 90 }} src={logo} />
							<img alt="banner" style={{ width: '80%', maxWidth: 280, filter: isDarkMode ? 'invert(1) hue-rotate(180deg) saturate(2)' : 'none' }} src={banner} />
						</Box>

						{/* Title */}
						<Typography variant="h6" fontWeight={600} sx={{ textAlign: 'center', width: '100%', color: isDarkMode ? 'white' : 'text.primary' }}>
							Anmelden
						</Typography>

						<Typography variant="body2" sx={{ textAlign: 'center', width: '100%', mb: 1, color: isDarkMode ? 'white' : 'text.primary' }}>
							Bitte gib deine Anmeldedaten ein
						</Typography>

						{/* Login Form */}
						<Box sx={{ width: '100%' }}>
							<LoginForm
								email={email}
								setEmail={setEmail}
								password={password}
								setPassword={setPassword}
								doLogin={doLogin}
								loginFailed={loginFailed}
								setLoginFailed={setLoginFailed}
								isDarkMode={isDarkMode} />
						</Box>

						{/* Login Button */}
						<Button
							variant="contained"
							onClick={doLogin}
							disabled={!email || !password}
							startIcon={<LoginOutlined />}
							fullWidth
							sx={{
								py: 1.2,
								color: 'white',
								backgroundColor: colors.login.button.bg,
								borderRadius: 2,
								textTransform: 'none',
								fontSize: '0.95rem',
								fontWeight: 600,
								boxShadow: colors.login.button.shadow,
								mt: 1,
								'&:hover': {
									backgroundColor: colors.login.button.hover,
									boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
									transform: 'translateY(-1px)',
									transition: 'background-color 0.2s ease, transform 0.2s ease'
								},
								'&:active': {
									transform: 'translateY(0)',
								},
								'&.Mui-disabled': {
									backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
									color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
								}
							}}
						>
							Anmelden
						</Button>

						{/* Help Button */}
						<Button
							onClick={handleHelpClick}
							startIcon={<HelpOutline />}
							sx={{
								color: isDarkMode ? colors.textSecondary : 'text.secondary',
								textTransform: 'none',
								borderRadius: 2,
								'&:hover': {
									color: colors.login.link.text,
									backgroundColor: 'transparent',
								}
							}}
						>
							Hilfe
						</Button>
					</Box>
				</Paper>
			) : (
				// Desktop View - Split Screen Design
				<Paper
					sx={{
						width: '100%',
						maxWidth: '1000px',
						minHeight: '550px',
						maxHeight: '90vh',
						overflow: 'hidden',
						borderRadius: 7,
						display: 'flex',
						flexDirection: 'row',
						position: 'relative',
						zIndex: 1,
						backgroundColor: isDarkMode ? 'transparent' : 'transparent', // Transparent container for split glass
						backdropFilter: 'none',
						border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none', // Border is on children or shadow
						boxShadow: isDarkMode ? '0 20px 60px rgba(0, 0, 0, 0.5)' : '0 20px 60px rgba(0, 0, 0, 0.15)',
						transition: 'background-color 0.2s ease',
					}}
				>
					{/* Left Side - Banner and Logo */}
					<Box
						sx={{
							width: '50%',
							background: isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
							p: 4,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 6,
							zIndex: 2,
							borderRight: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
							boxShadow: isDarkMode
								? '8px 0 25px rgba(0, 0, 0, 0.4), inset -1px 0 0 rgba(255, 255, 255, 0.05)'
								: '8px 0 30px rgba(0, 0, 0, 0.12), inset -1px 0 0 rgba(255, 255, 255, 0.5)',
							transition: 'background-color 0.2s ease, border-color 0.2s ease',
						}}
					>
						<img alt="banner" style={{ width: '90%', maxWidth: 400, filter: isDarkMode ? 'invert(1) hue-rotate(180deg) saturate(2)' : 'none' }} src={banner} />
						<img alt="logo" style={{ width: 200, height: 200 }} src={logo} />
					</Box>

					{/* Right Side - Login Form */}
					<Box
						sx={{
							width: '50%',
							p: 4,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							overflow: 'auto',
							backgroundColor: isDarkMode ? 'rgba(20, 20, 20, 0.4)' : 'rgba(255, 255, 255, 0.3)',
							border: isDarkMode ? 'none' : '1px solid rgba(20, 20, 20, 0.08)',
							borderLeft: 'none',
							borderRadius: isDarkMode ? 0 : '0 28px 28px 0',
							boxShadow: isDarkMode
								? 'inset 3px 0 15px rgba(0, 0, 0, 0.3)'
								: 'inset 3px 0 15px rgba(0, 0, 0, 0.10)',
							position: 'relative',
						}}
					>
						{/* Dark Mode Toggle Button (Desktop) */}
						<IconButton
							onClick={toggleDarkMode}
							sx={{
								position: 'absolute',
								top: 24,
								right: 24,
								zIndex: 10,
								backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
								color: isDarkMode ? colors.accentLight : 'text.secondary',
								border: '1px solid',
								borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
								boxShadow: 'none',
								'&:hover': {
									backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
									transform: 'scale(1.05)',
								},
								transition: 'background-color 0.2s ease, transform 0.2s ease',
							}}
						>
							{isDarkMode ? <LightModeOutlined /> : <DarkModeOutlined />}
						</IconButton>
						<Typography variant="h5" fontWeight={600} sx={{ mb: 1, textAlign: 'center', color: isDarkMode ? 'white' : 'text.primary' }}>
							Anmelden
						</Typography>

						<Typography variant="body2" sx={{ mb: 4, textAlign: 'center', color: isDarkMode ? 'white' : 'text.primary' }}>
							Bitte gib deine Anmeldedaten ein
						</Typography>

						<Box sx={{ mb: 3 }}>
							<LoginForm
								email={email}
								setEmail={setEmail}
								password={password}
								setPassword={setPassword}
								doLogin={doLogin}
								loginFailed={loginFailed}
								setLoginFailed={setLoginFailed}
								isDarkMode={isDarkMode} />
						</Box>

						<Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
							<Button
								variant="contained"
								onClick={doLogin}
								disabled={!email || !password}
								startIcon={<LoginOutlined />}
								fullWidth
								sx={{
									maxWidth: '75%',
									py: 1.3,
									color: 'white',
									backgroundColor: colors.login.button.bg,
									borderRadius: 2,
									textTransform: 'none',
									fontSize: '1rem',
									fontWeight: 600,
									boxShadow: colors.login.button.shadow,
									'&:hover': {
										backgroundColor: colors.login.button.hover,
										boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
										transform: 'translateY(-1px)',
										transition: 'background-color 0.2s ease, transform 0.2s ease'
									},
									'&:active': {
										transform: 'translateY(0)',
									},
									'&.Mui-disabled': {
										backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
										color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
									}
								}}
							>
								Anmelden
							</Button>
						</Box>

						<Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
							<Button
								onClick={handleHelpClick}
								startIcon={<HelpOutline />}
								sx={{
									color: isDarkMode ? colors.textSecondary : 'text.secondary',
									textTransform: 'none',
									borderRadius: 2,
									'&:hover': {
										color: colors.login.link.text,
										backgroundColor: 'transparent',
									}
								}}
							>
								Hilfe
							</Button>
						</Box>
					</Box>
				</Paper>
			)}			<Popover
				open={openHelp}
				anchorEl={anchorEl}
				onClose={handleHelpClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'center',
				}}
				PaperProps={{
					sx: {
						p: mobile ? 2 : 2.5,
						borderRadius: 5,
						maxWidth: mobile ? '70vw' : 400,
						width: mobile ? 'calc(100vw - 32px)' : 'auto',
						boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
						backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'white',
						border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
						color: isDarkMode ? 'white' : 'text.primary',
					}
				}}
			>
				<Typography variant="body2" sx={{
					color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary',
					lineHeight: 1.8,
					'& a': {
						color: colors.login.link.text,
						textDecoration: 'none',
						fontWeight: 500,
						'&:hover': {
							textDecoration: 'underline',
							color: colors.login.link.hover,
						}
					}
				}}>
					Solltest du noch keinen Account besitzen, wende dich bitte an den&nbsp;
					<a href={`mailto:${NATIONAL_COACH_EMAIL}`}>
						Bundestrainer
					</a>
					.
					<br /><br />
					Release 4.0 • March 2025 • Report bugs and suggestions to&nbsp;
					<a href={`mailto:markus.kukuk@gmail.com`}>
						Markus Kukuk
					</a>
				</Typography>
			</Popover>
		</Box>
	);
}

export default Login;